import { useCallback } from 'react';
import type { SocketStatusChangeEvent, UnionOfMessagePayloads } from '@m43/sockets-mark43';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import type { StreamMap } from '../socket/stream-types';
import { SENSOR_ALERT_STREAM_NAMES } from './constants';
import { SensorsStore } from './sensors-store';
import { useSensorBootstrap } from './use-sensor-bootstrap';

/**
 * Wires the sensor store to the server: the bootstrap call, and the live `sensorAlert` stream.
 *
 * The stream is fire-and-forget, so alerts published while the socket was down are never
 * delivered. A reconnect re-runs the bootstrap to catch up, as the queues do; the alerts it
 * returns ingest as not live, so the catch-up raises no toasts.
 *
 * Mounted only where sensors are on (see `SensorsProvider`), so it has no `enabled` switch.
 */
export function useIntegratedSensors({ store }: { store: SensorsStore }): void {
    const bootstrapQuery = useSensorBootstrap(store);

    const upsertLiveAlert = useCallback(
        function upsertLiveAlert(
            message: UnionOfMessagePayloads<StreamMap, typeof SENSOR_ALERT_STREAM_NAMES[number]>
        ) {
            // The only live ingest path. `isLive` is what lets the store tell a new alert from a
            // link update, and what keeps the catch-up from toasting.
            store.upsertAlerts([message.payload], { isLive: true });
        },
        [store]
    );

    const refetchBootstrap = bootstrapQuery.refetch;
    const catchUpAfterReconnect = useCallback(
        function catchUpAfterReconnect(event: SocketStatusChangeEvent<StreamMap, keyof StreamMap>) {
            // The first connection is covered by the bootstrap query's own fetch.
            return event.timesConnected > 1 ? refetchBootstrap() : Promise.resolve();
        },
        [refetchBootstrap]
    );

    useIntegratedStreams({
        query: bootstrapQuery,
        streamNames: SENSOR_ALERT_STREAM_NAMES,
        onMessage: upsertLiveAlert,
        onReconnect: catchUpAfterReconnect,
    });
}
